// React
import { useState } from 'react'

// Mantine
import { Button, Select, Modal } from '@mantine/core'
import { DatePickerInput } from '@mantine/dates'
import { useForm, zodResolver } from '@mantine/form'

// Zod
import { z } from 'zod'

// dayjs
import dayjs from 'dayjs'

// UI Components
import LocationSelect from '@/components/ui/LocationSelect'
import AddressForm from '@/components/ui/AddressForm'
import ShopifyVariantSelect from '@/components/ui/ShopifyVariantSelect'
import VariantLine from '@/components/lines/VariantLine'
import UiBox from '../ui/Box'

// Hooks
import { useVariantLines } from '@/hooks/useVariantLines'

// Schemas
const schema = z.object({
    fulfill_at: z.date(),
    location_id: z.string(),
    warranties_mode: z.string(),
    consignee: z.object({
        name: z.string(),
        email: z.string(),
        phone: z.string(),
        company: z.string(),
        address1: z.string(),
    }),
})

// Constants
const WARRANTY_MODES = [
    { value: 'associate', label: 'Associer les produits garantissables' },
    { value: 'blank', label: 'Ajouter des cartes de garanties vierges' },
    { value: 'none', label: 'Ne pas ajouter de cartes de garanties' },
]

const PREPARATION_MODES = [
    { value: 'shipping', label: 'Expédition par transporteur' },
    { value: 'pickup', label: 'Remise en main propre' },
]

export default function FulfillmentOrderCreateEdit({
    fulfillment_order = null,
    onSubmit,
}) {
    // State
    const [consigneModalOpened, setConsigneModalOpened] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)

    const [initialVariantIds, setInitialVariantIds] = useState(
        fulfillment_order?.lines.map(
            (l) => l.shopify_product_variant.id
        ) ?? []
    )

    // Hooks
    const {
        lines,
        loading: isLinesLoading,
        addVariant,
        updateLinePrice,
        updateLineQuantity,
        removeLine,
    } = useVariantLines(fulfillment_order?.lines)

    // Forms
    const form = useForm({
        validateInputOnChange: true,
        validate: zodResolver(schema),
        initialValues: {
            location_id: fulfillment_order?.location_id
                ? String(fulfillment_order.location_id)
                : '1',
            fulfill_at: fulfillment_order?.fulfill_at
                ? new Date(fulfillment_order?.fulfill_at)
                : dayjs().add(1, 'day').toDate(),
            warranties_mode: fulfillment_order?.warranties_mode ?? 'associate',
            consignee: {
                name: fulfillment_order?.consignee?.name ?? null,
                email: fulfillment_order?.consignee?.email ?? null,
                address1: fulfillment_order?.consignee?.address1 ?? null,
                address2: fulfillment_order?.consignee?.address2 ?? null,
                city: fulfillment_order?.consignee?.city ?? null,
                country_code:
                    fulfillment_order?.consignee?.country_code ?? null,
                company: fulfillment_order?.consignee?.company ?? null,
                phone: fulfillment_order?.consignee?.phone ?? null,
                province_code:
                    fulfillment_order?.consignee?.province_code ?? null,
                zip: fulfillment_order?.consignee?.zip ?? null,
                tax_id: fulfillment_order?.consignee?.tax_id ?? null,
            },
        },
    })

    // Handlers
    const handleSubmit = form.onSubmit(async (values) => {
        setIsSubmitting(true)

        onSubmit({
            fulfillmentOrderFormValue: {
                fulfill_at: values.fulfill_at,
                location_id: values.location_id,
                consignee: values.consignee,
                status: 'OPEN',
            },
            linesFormValue: lines.map((line) => ({
                variant_id: line.variant_id ?? null,
                total_quantity: line.quantity,
                unit_price_initial: line.unit_price,
                remaining_quantity: line.quantity,
                requires_shipping: true,
                ...(line.id && { id: line.id }),
                ...(fulfillment_order && {
                    fulfillment_order_id: fulfillment_order.id,
                }),
            })),
        })
    })

    const handleConsigneeSubmit = (values) => {
        form.setFieldValue('consignee', values)
        setConsigneModalOpened(false)
    }

    const handleAddVariant = (variantToAdd) => {
        addVariant(variantToAdd.id)
    }

    // Computed
    const isFulfillmentOrderValid = form.isValid() && lines.length > 0

    // Render
    return (
        <form
            onSubmit={handleSubmit}
            className="w-full mx-auto max-w-[1200px] gap-5 grid grid-cols-[2fr_1fr] items-start"
        >
            <div className="flex flex-col gap-5">
                <UiBox
                    className="bg-white border p-5 rounded-sm flex flex-col gap-2"
                    title="Produits à préparer"
                >
                    {form.errors.lines && (
                        <span className="text-red-500">
                            {form.errors.lines}
                        </span>
                    )}

                    <div className="flex flex-col gap-5">
                        {lines.length > 0 && (
                            <div className="flex flex-col gap-5 border rounded-xl p-5 divide-y divide-gray-200">
                                {lines.map((line, index) => (
                                    <VariantLine
                                        key={line.variant_id}
                                        line={line}
                                        index={index}
                                        onPriceChange={(price) => {
                                            updateLinePrice(index, price)
                                        }}
                                        onQuantityChange={(quantity) =>
                                            updateLineQuantity(index, quantity)
                                        }
                                        onRemove={() => removeLine(index)}
                                    />
                                ))}
                            </div>
                        )}

                        {lines.length === 0 && (
                            <div className="border flex justify-center p-5 bg-gray-50 text-gray-400 text-sm text-center rounded-xl">
                                Aucune ligne
                            </div>
                        )}

                        <ShopifyVariantSelect
                            label="Ajouter une variante"
                            onSelect={handleAddVariant}
                        />
                    </div>
                </UiBox>

                <UiBox title="Préparation">
                    <div className="flex flex-col gap-2">
                        <DatePickerInput
                            label="Date de préparation"
                            description="La date à partir de laquelle la préparation doit être effectuée"
                            placeholder="Choisir une date de préparation..."
                            minDate={new Date()}
                            {...form.getInputProps('fulfill_at')}
                            locale="fr"
                            valueFormat="LL"
                        />

                        <LocationSelect
                            label="Lieu de préparation"
                            placeholder="Choisir un lieu de préparation..."
                            {...form.getInputProps('location_id')}
                        />

                        {false && (
                            <Select
                                label="Mode de préparation"
                                placeholder="Choisir un mode de préparation..."
                                data={PREPARATION_MODES}
                            />
                        )}

                        <Select
                            label="Cartes de garantie"
                            placeholder="Indiquer si les cartes de garantie doivent être associées"
                            {...form.getInputProps('warranties_mode')}
                            data={WARRANTY_MODES}
                        />
                    </div>
                </UiBox>
            </div>

            <div className="flex flex-col gap-5">
                <UiBox title="Destinataire">
                    <div className="flex flex-col gap-3">
                        <div className="flex flex-col">
                            <span>{form.values.consignee.name}</span>
                            <span>{form.values.consignee.email}</span>
                            <span>{form.values.consignee.phone}</span>
                            <span>{form.values.consignee.company}</span>
                            <span>{form.values.consignee.address1}</span>
                            <span>{form.values.consignee.address2}</span>
                            <span>
                                {form.values.consignee.zip}{' '}
                                {form.values.consignee.city}
                            </span>
                            <span>{form.values.consignee.country_code}</span>
                        </div>

                        <Modal
                            title="Destinataire"
                            opened={consigneModalOpened}
                            onClose={() => setConsigneModalOpened(false)}
                            size="lg"
                        >
                            <AddressForm
                                initialValues={form.values.consignee}
                                onSubmit={handleConsigneeSubmit}
                            />
                        </Modal>

                        <Button
                            variant="default"
                            onClick={() => setConsigneModalOpened(true)}
                        >
                            Modifier le destinataire
                        </Button>
                    </div>
                </UiBox>

                <Button
                    type="submit"
                    loading={isSubmitting}
                    disabled={!isFulfillmentOrderValid}
                >
                    Enregistrer la demande
                </Button>
            </div>
        </form>
    )
}
